// @ts-strict-ignore
import type { ValidationRejection } from "@altostra/type-validations";

import { Disposer } from "@lib/disposer";
import { FetchInvalidPayloadShapeError, FetchRequestError } from "@lib/errors";
import { PUSHER_CONFIG } from "@lib/permissionConstants";

import { PUSHER_ERROR_STATUS, PUSHER_SUBSCRIPTION_SUCCEEDED } from "../constants";
import type { AllRouteEndpoints } from "../routeTypes";
import type {
  GetPostWebsocketFetcherFunctionWithShadowError,
  InternalPostWebsocketFetcherFunctionWithShadowError,
} from "../types";
import type { BaseAsyncProps, EndpointQueryKey, PusherDeveloperBindings } from "./types";
import {
  decompressPusherResponseV2,
  isPusherError,
  logToDataDog,
  getEventName,
  registerChunkSubscription,
  mergeChunkedPayloadIfNeeded,
  resolveChunkedMergeConfig,
} from "./utils";

/**
 * Adapter from react-query fetcher function to a more natural fetcher function.
 *
 * This takes the fetcher function and passes in the query key, minus the root cache key
 * to the function. It also passes the abort signal as an optional last parameter
 */
export const reactQueryPostWebsocketAdapter = <
  CacheKey extends AllRouteEndpoints,
  Arguments extends BaseAsyncProps,
  Result,
  TError extends FetchRequestError = FetchRequestError,
>(
  cacheKey: CacheKey,
  fetcherFunction: InternalPostWebsocketFetcherFunctionWithShadowError<TError, Arguments>,
  validatePusherResult: (
    result: unknown,
    rejectionReasons?: (rejection: ValidationRejection) => void
  ) => result is Result
): GetPostWebsocketFetcherFunctionWithShadowError<TError, CacheKey, Arguments, Result> => {
  // convert jobToken into an optional property, so we can use it in faking if necessary
  return async (
    {
      queryKey: [{ cacheKey: endpoint }, args],
      signal,
      meta,
    }: EndpointQueryKey<CacheKey, [Omit<Arguments, "jobToken"> & { jobToken?: string }]>,
    customizedFetcherFunction?: Function
  ) => {
    // If customizedFetcher is provided, we use that instead of the default fetcherFunction
    if (customizedFetcherFunction) {
      const jobToken = args.jobToken;
      const augmentedArgs: Arguments = { ...args, jobToken } as Arguments;
      return customizedFetcherFunction(signal, augmentedArgs);
    }

    const extractedRequestId = (args as any)?.meta?.requestId || undefined;

    // We register multiple pusher bindings that need to be cleaned up when the
    // fetch is done, so we use a Disposer to manage them.
    const disposer = new Disposer();

    const pusher = meta.pusher as PusherDeveloperBindings;
    const logException = meta.logException as ((error: Error) => void) | undefined;

    const jobToken = args.jobToken;
    const eventName = getEventName(jobToken);

    const chunkedMergeConfig = resolveChunkedMergeConfig(args.chunkedMerge);
    const chunkState = registerChunkSubscription({
      enabled: !!chunkedMergeConfig,
      pusher,
      jobToken,
      onCleanup: (fn) => disposer.add(fn),
    });

    // Creates a promise that resolves or rejects based on pusher events sent
    // to the passed callback. Will unbind when dispose is called.
    const createPusherPromise = <T = unknown>(
      eventName: string,
      callback: (data: unknown, resolve: (value: T) => void, reject: (error: unknown) => void) => void
    ): Promise<T> => {
      return new Promise<T>((resolve, reject) => {
        // Note that promise bodies are executed synchronously, so this binding
        // is registered before the fetcherFunction is called.
        const pusherCallback = (data: unknown) => {
          callback(data, resolve, reject);
        };
        pusher.bind(eventName, pusherCallback);
        disposer.add(() => pusher.unbind(eventName, pusherCallback));
      });
    };

    try {
      // listen for the result of the async process and return it
      const successPromise = createPusherPromise(eventName, (data, resolve) => {
        resolve(data);
      });

      // listen for pusher disconnect/reconnect, which results in the need to start over
      const reconnectPromise = createPusherPromise<void>(
        PUSHER_SUBSCRIPTION_SUCCEEDED,
        (_data, _resolve, reject) => {
          reject(new DOMException("", "AbortError"));
        }
      );

      // listen for request cancellation from AsyncRequestManager
      const cancellationPromise = createPusherPromise<void>(
        PUSHER_CONFIG.segmentStatsCancellationUpdate,
        (data, _resolve, reject) => {
          // Should only cancel if the request IDs match
          const cancelledRequestId =
            typeof data === "object" && data !== null && "request_id" in data ? data.request_id : undefined;
          if (extractedRequestId && cancelledRequestId && extractedRequestId === cancelledRequestId) {
            reject(new DOMException("", "AbortError"));
          }
        }
      );

      // listen for abort signal, which should also account for timeouts given we've added it to the signal.
      const abortPromise = signal
        ? new Promise<void>((_, reject) => {
            if (signal.aborted) {
              return reject(new DOMException("", "AbortError"));
            }
            const onAbort = () => {
              reject(new DOMException("", "AbortError"));
            };
            signal.addEventListener("abort", onAbort, { once: true });
            disposer.add(() => signal.removeEventListener("abort", onAbort));
          })
        : null;

      // listen for timeout if provided
      const timeoutPromise = args.timeout
        ? new Promise((_, reject) => {
            const timeout = setTimeout(() => {
              reject(new FetchRequestError(504, "Request timeout"));
            }, args.timeout);
            disposer.add(() => clearTimeout(timeout));
          })
        : null;

      // for datadog logging
      const startTime = Date.now();
      const { estimated } = args;
      const augmentedArgs: Arguments = { ...args, jobToken } as Arguments;

      const promisesToRace = [
        successPromise,
        reconnectPromise,
        cancellationPromise,
        abortPromise,
        timeoutPromise,
      ].filter(Boolean);

      // Mark all promises-to-race as handled so that their errors don't bubble
      // up to the top-level as an unhandled promise rejection.
      for (const promise of promisesToRace) {
        promise.catch(() => {});
      }

      await fetcherFunction(signal, augmentedArgs);

      const result = await Promise.race(promisesToRace);

      let pusherResponse = decompressPusherResponseV2<Result>(result);
      pusherResponse = await mergeChunkedPayloadIfNeeded<Result>(chunkedMergeConfig, pusherResponse, chunkState);
      logToDataDog({
        actionName: isPusherError(pusherResponse) ? "pusher-request-fetch-error" : "pusher-request-fetch-resolve",
        url: endpoint,
        errorText: isPusherError(pusherResponse) ? pusherResponse.pusher_error : "ok",
        loadDuration: Date.now() - startTime,
        estimated: estimated ? true : false,
      });
      if (isPusherError(pusherResponse)) {
        throw new FetchRequestError(PUSHER_ERROR_STATUS, pusherResponse.pusher_error);
      } else {
        FetchInvalidPayloadShapeError.validateShape({
          cacheKey,
          result: pusherResponse,
          validator: validatePusherResult,
          statusCode: PUSHER_ERROR_STATUS,
          rejectionMessage: "Invalid pusher response",
          logException,
        });

        return pusherResponse as Result;
      }
    } finally {
      disposer.dispose();
    }
  };
};
